// The owner's first screen (DASH-01): what needs attention right now; then, for the days chosen (Today unless the
// address says otherwise), the day's numbers, the agents side by side, everyone's calls through the day and one person's
// day hour by hour (D-144); then the week's objections and how calls do on the company's checklist (DASH-05). A team of
// one (D-169) sees her own progress instead: her week against the same days last week, her calls week by week, her day.
import { Link, useSearchParams } from "react-router";
import { ActivityHeatmap, AgentCompare, AgentDay, DateFilter, RangeStats } from "../app/dashboard";
import { doneShare, teamTally } from "../lib/aiInstructions";
import { fmtAgo, fmtNumber, fmtTime, isAdminRole, isManagerRole, localDate } from "../lib/format";
import { addDays, dayCount, rangeDates, rangeFromParams, rangeLabel, rangeParams, shortDay } from "../lib/range";
import { callsByWeek, weekStart, weekStats, weekWindows } from "../lib/progress";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, Stat, useLoad } from "../app/ui";

const PLATFORMS: Record<string, string> = { fb: "Facebook", ig: "Instagram", an: "Audience Network", msg: "Messenger" };

/** The leads of the last 24 hours, newest first (D-149): where from, how long ago, who has them, called yet. */
function NewLeads({ manager }: { manager: boolean }) {
  const { api, tz } = useMember();
  const recent = useLoad(() => api.recentEnquiries(24), [api]);
  const d = recent.data;
  if (!d) return <ErrorNote error={recent.error} />;
  const now = Date.now();
  return (
    <Card
      title={manager ? "New leads, last 24 hours" : "Your new leads, last 24 hours"}
      actions={d.total > 0 ? <span className="faint">{d.total} in · {d.not_called} not called yet</span> : null}
    >
      {d.total === 0 ? <Empty>No new lead in the last 24 hours.</Empty> : (
        <div className="list">
          {d.items.slice(0, 10).map((e) => {
            const platform = e.platform ? PLATFORMS[e.platform] ?? e.platform : null;
            const from = [
              e.source && platform && !e.source.toLowerCase().includes(platform.toLowerCase())
                ? `${e.source} · ${platform}`
                : e.source ?? platform,
              e.form ?? e.campaign,
            ].filter(Boolean).join(" · ");
            return (
              <Link className="list-item new-lead" to={`/leads/${e.lead_id}`} key={e.enquiry_id}>
                <div>
                  <strong>{e.name}</strong> {e.new_customer && <span className="badge brand">New</span>}
                  {from && <div className="faint">{from}</div>}
                </div>
                <div className="new-lead-when">
                  <div>{fmtAgo(e.enquired_at, tz, now)}</div>
                  <div className={e.called_at ? "faint" : "danger-text"}>
                    {e.called_at ? `Called ${fmtTime(e.called_at, tz)}` : "Not called yet"}
                    {manager ? ` · ${e.owner ?? "nobody"}` : ""}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function greeting(tz: string): string {
  const h = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      hourCycle: "h23",
      timeZone: tz,
    }).format(new Date()),
  );
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

export function Overview() {
  const { api, member, tz } = useMember();
  const solo = member.member_limit === 1;
  const manager = isManagerRole(member.role) && !solo;
  const [params, setParams] = useSearchParams();
  const today = localDate(new Date(), tz);
  const range = rangeFromParams(params, today);
  const multiDay = dayCount(range.from, range.to) > 1;
  const overview = useLoad(() => api.overview(), [api]);
  const days = useLoad(() => api.dashboardRange(range.from, range.to), [api, range.from, range.to], true);
  const data = overview.data;

  return (
    <Page
      title={`${greeting(tz)}, ${member.display_name.split(" ")[0]}`}
      sub={manager ? member.org_name : "Your numbers"}
    >
      <ErrorNote error={overview.error} />
      <div className="stack">
        {manager && data && data.week.calls === 0 && <GettingStarted />}
        {solo && data && data.week.calls === 0 && <GettingStartedAlone />}
        <section className="stack tight" aria-label="Right now">
          <h2 className="section-title">Right now</h2>
          {overview.loading && !data ? <Loading /> : data && (
            <div className="grid cols-4">
              <Stat
                label="Overdue follow-ups"
                value={fmtNumber(data.now.overdue_tasks)}
                alert={data.now.overdue_tasks > 0}
                href="#/today"
                hint={manager ? "Across the team" : "Yours"}
              />
              <Stat label="Due today" value={fmtNumber(data.now.due_today)} href="#/today" />
              <Stat
                label="Missed calls not returned"
                value={fmtNumber(data.now.unreturned_missed_calls)}
                alert={data.now.unreturned_missed_calls > 0}
                href="#/today"
              />
              <Stat
                label="Hot leads open"
                value={fmtNumber(data.now.hot_leads_open)}
                href="#/leads?ai=hot&status=open"
              />
            </div>
          )}
          <NewLeads manager={manager} />
        </section>

        {solo && <YourWeek />}

        <section className="stack" aria-label={rangeLabel(range)}>
          <div className="filter-row">
            <DateFilter
              key={`${range.from}|${range.to}|${range.preset}`}
              range={range}
              today={today}
              onChange={(r) => setParams(rangeParams(r), { replace: true })}
            />
            <span className="faint">{rangeDates(range)}</span>
          </div>
          <ErrorNote error={days.error} />
          {days.loading && !days.data && <Loading />}
          {days.data && (
            <div className={`stack${days.loading ? " refetching" : ""}`}>
              <RangeStats members={days.data.members} />
              {manager && <AgentCompare data={days.data} tz={tz} multiDay={multiDay} />}
              {manager && <ActivityHeatmap data={days.data} multiDay={multiDay} />}
              <AgentDay data={days.data} multiDay={multiDay} manager={manager} />
            </div>
          )}
        </section>

        <section className="stack" aria-label="Last 7 days">
          <h2 className="section-title">Last 7 days</h2>
          {manager && <RecordingStats />}
          {data && (
            <Card title="Top objections">
              {data.top_objections.length === 0 && <Empty>No objections heard yet this week.</Empty>}
              {data.top_objections.map((o) => {
                const max = data.top_objections[0].count;
                return (
                  <Link className="hbar" key={o.category} to={`/objections/${encodeURIComponent(o.category)}`}>
                    <span>{o.label}</span>
                    <div className="track">
                      <div style={{ width: `${(o.count / max) * 100}%` }} />
                    </div>
                    <span className="num">{o.count}</span>
                  </Link>
                );
              })}
              {data.top_objections.length > 0 && (
                <p className="faint" style={{ marginTop: 12 }}>
                  Open an objection to hear each time it was raised.
                </p>
              )}
            </Card>
          )}
          <ChecklistCard manager={manager} />
        </section>
      </div>
    </Page>
  );
}

/**
 * How often each checklist item was done in the last 7 days, per person (DASH-05). An item counts only on calls it
 * applied to; a telecaller sees only their own column.
 */
function ChecklistCard({ manager }: { manager: boolean }) {
  const { api, member } = useMember();
  const { data } = useLoad(
    () => Promise.all([api.checklistSummary(7), api.aiInstructions(), api.members()]),
    [api],
  );
  if (!data || data[0].length === 0) return null;
  const [rows, ins, members] = data;
  const names = new Map(members.map((m) => [m.id, m.display_name]));
  const people = [...new Set(rows.map((r) => r.member_id))]
    .sort((a, b) => (names.get(a) ?? "").localeCompare(names.get(b) ?? ""));
  // Today's items first, in the company's order; then items since removed that still have results.
  const items = [
    ...ins.checklist.map((i) => ({ key: i.key, label: i.label })),
    ...[...new Set(rows.map((r) => r.item))].filter((k) => !ins.checklist.some((i) => i.key === k))
      .map((k) => ({ key: k, label: k.replace(/_/g, " ") })),
  ].filter((i) => rows.some((r) => r.item === i.key));
  const cell = (t: { done: number; missed: number } | undefined) => {
    const share = doneShare(t);
    if (share === null || !t) return <span className="faint">–</span>;
    return (
      <span className={`tally${share < 0.5 ? " low" : ""}`}>
        <strong>{Math.round(share * 100)}%</strong> <span className="faint">{t.done}/{t.done + t.missed}</span>
      </span>
    );
  };

  return (
    <Card title={manager ? "Call checklist, last 7 days" : "Your call checklist, last 7 days"} flush>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              {manager && <th className="num">Team</th>}
              {people.map((id) => <th className="num" key={id}>{(names.get(id) ?? "Someone").split(" ")[0]}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.key}>
                <td>{item.label}</td>
                {manager && <td className="num">{cell(teamTally(rows, item.key))}</td>}
                {people.map((id) => (
                  <td className="num" key={id}>
                    {cell(rows.find((r) => r.member_id === id && r.item === item.key))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ margin: "10px 16px 14px" }}>
        Share of calls where the item applied and the caller did it. Open a call to hear the moment.
        {isAdminRole(member.role) && (
          <>
            {" "}Edit the checklist in <Link to="/ai">AI instructions</Link>.
          </>
        )}
      </p>
    </Card>
  );
}

/** A team of one's week (D-169): six numbers against the same days last week, and her calls each week for eight. */
function YourWeek() {
  const { api, tz } = useMember();
  const today = localDate(new Date(), tz);
  const w = weekWindows(today);
  const trendFrom = addDays(weekStart(today), -7 * 7);
  const { data, error } = useLoad(
    () =>
      Promise.all([
        api.dashboardRange(w.now.from, w.now.to),
        api.dashboardRange(w.before.from, w.before.to),
        api.dashboardRange(trendFrom, today),
      ]),
    [api, today],
  );
  if (!data) return <ErrorNote error={error} />;
  const [now, before, trend] = data;
  const weeks = callsByWeek(trend.days, trendFrom, 8);
  const most = Math.max(1, ...weeks.map((x) => x.calls));
  return (
    <section className="stack" aria-label="Your week">
      <h2 className="section-title">Your week, against the same days last week</h2>
      <div className="grid cols-3">
        {weekStats(now.members, before.members).map((s) => (
          <Stat
            key={s.label}
            label={s.label}
            value={s.value}
            hint={s.change.text && (
              <span className={s.change.better === false ? "danger-text" : s.change.better ? "ok-text" : undefined}>
                {s.change.text}
              </span>
            )}
          />
        ))}
      </div>
      <Card title="Your calls each week">
        <div className="week-bars" role="img" aria-label="Calls each week, the last eight weeks">
          {weeks.map((x) => (
            <div key={x.week} title={`Week of ${shortDay(x.week)}: ${x.calls} calls, ${x.connected} connected`}>
              <span className="num">{x.calls}</span>
              <div className="bar" style={{ height: `${(x.calls / most) * 100}%` }} />
              <span className="faint">{shortDay(x.week)}</span>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}

/** A team of one before her first calls: her phone, then her hours. */
function GettingStartedAlone() {
  return (
    <Card title="Getting started">
      <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
        <li>Install the Voxowa app on your phone and sign in with your mobile number.</li>
        <li>Allow what it asks for, so your calls reach your customer list.</li>
        <li>
          <Link to="/settings">Set your working hours</Link> and add personal numbers that must never be recorded.
        </li>
      </ol>
      <p className="faint" style={{ marginTop: 8 }}>
        Your numbers appear here as you call. New customers from your lead sources come straight to your Today list.
      </p>
    </Card>
  );
}

/** Until the first calls arrive: the three steps that get a team going. */
function GettingStarted() {
  return (
    <Card title="Getting started">
      <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
        <li>
          <Link to="/team">Invite your telecallers</Link> by their mobile numbers.
        </li>
        <li>
          Each telecaller installs the Voxowa app, signs in with that number, accepts the recording notice and turns on
          call recording in their dialer.
        </li>
        <li>
          <Link to="/settings">Set your working hours</Link> and add personal numbers that must never be recorded.
        </li>
      </ol>
      <p className="faint" style={{ marginTop: 8 }}>
        Calls appear here within minutes of being made; follow-ups appear as each call is analysed.
      </p>
    </Card>
  );
}

/** How many recordings the phones sent and how far they got (D-077); for now, to watch a company's first days. */
function RecordingStats() {
  const { api } = useMember();
  const { data } = useLoad(() => api.recordingCounts(), [api]);
  if (!data) return null;
  return (
    <div className="grid cols-4">
      <Stat
        label="Recordings received"
        value={fmtNumber(data.received)}
        href="#/calls"
        hint={data.without_customer > 0
          ? `${fmtNumber(data.without_customer)} processed without a customer`
          : undefined}
      />
      <Stat label="Processed" value={fmtNumber(data.processed)} href="#/calls" />
      <Stat label="Waiting" value={fmtNumber(data.waiting)} href="#/calls" />
      <Stat label="Failed" value={fmtNumber(data.failed)} alert={data.failed > 0} href="#/calls" />
    </div>
  );
}
